import { Box, Divider, List, Popover, Typography } from "@mui/material";
import { ActionButton } from "@zfp-akademie-a-s/zfp-design";
import { ChevronDownCircleIcon } from "Components/_Shared/Icons/Icons";
import type { AnalysisVersionInfo } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { type ReactNode, useContext, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnalysisDetailContext } from "./AnalysisDetailContext";
import { VersionSelectItem } from "./VersionSelectItem";

const SectionTitle = ({ id, children }: { readonly id: string; readonly children: ReactNode }) => (
  <Typography
    id={id}
    display="block"
    px={Spacing.XL}
    py={Spacing.S}
    fontWeight={400}
    variant="caption"
    color="secondary"
    textTransform="uppercase"
  >
    {children}
  </Typography>
);

type Props = {
  readonly versions: AnalysisVersionInfo[];
};

export const VersionSelect = ({ versions }: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();
  const location = useLocation();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const popoverId = useId();
  const latestTitleId = useId();
  const previousTitleId = useId();
  const displayedRowRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const latestVersion = versions.find((version) => !version.readOnly);
  const displayedVersion = versions.find((version) => version.versionId === versionId);
  const previousVersions = versions.filter((version) => version.readOnly).sort((prev, next) => next.version - prev.version);
  const isOpen = !!anchorEl;

  const handleClose = () => setAnchorEl(null);

  const handleOpenVersion = (newVersionId: string) => {
    handleClose();

    navigate(`${AppRouting.AnalysisDetail.path(analysisId, newVersionId)}${location.search}`);
  };

  if (!latestVersion || !displayedVersion) return null;

  const getRowRef = (version: AnalysisVersionInfo) => (version.versionId === versionId ? displayedRowRef : undefined);

  return (
    <>
      <ActionButton
        label={t(Resources.AnalysisDetail.VersionSelect.Tooltip)}
        text={
          <>
            {t(Resources.AnalysisDetail.VersionSelect.Trigger, { version: displayedVersion.version })}
            {displayedVersion.readOnly && (
              <Box component="span" fontWeight={400}>
                {` ${t(Resources.AnalysisDetail.VersionSelect.Older)}`}
              </Box>
            )}
          </>
        }
        icon={<ChevronDownCircleIcon />}
        iconPosition="end"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={isOpen ? popoverId : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      />
      <Popover
        id={popoverId}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { mt: 1, width: 400, maxHeight: 480, p: Spacing.M, borderRadius: Radius.L, border: "1px solid", borderColor: "divider" },
          },
          // the popover itself takes the focus on open, the displayed version gets it once the popover is in place
          transition: { onEntered: () => displayedRowRef.current?.focus() },
        }}
      >
        <List disablePadding aria-label={t(Resources.AnalysisDetail.VersionSelect.Label)}>
          <Box component="li">
            <SectionTitle id={latestTitleId}>{t(Resources.AnalysisDetail.VersionSelect.Latest)}</SectionTitle>
            <List disablePadding aria-labelledby={latestTitleId}>
              <VersionSelectItem
                version={latestVersion}
                isDisplayed={latestVersion.versionId === versionId}
                buttonRef={getRowRef(latestVersion)}
                onOpen={handleOpenVersion}
              />
            </List>
          </Box>
          {previousVersions.length > 0 && (
            <Box component="li">
              <Divider sx={{ my: Spacing.S }} />
              <SectionTitle id={previousTitleId}>{t(Resources.AnalysisDetail.VersionSelect.Previous)}</SectionTitle>
              <List disablePadding aria-labelledby={previousTitleId}>
                {previousVersions.map((version) => (
                  <VersionSelectItem
                    key={version.versionId}
                    version={version}
                    isDisplayed={version.versionId === versionId}
                    buttonRef={getRowRef(version)}
                    onOpen={handleOpenVersion}
                  />
                ))}
              </List>
            </Box>
          )}
        </List>
      </Popover>
    </>
  );
};
